<i18n src="@/locales/modules/TodoListTranslate.json"></i18n>

<template>
	<div class="quick-config-todo-item">
		<div>
			<!-- NAME -->
			<label class="vs-input--label">
				{{ $t("Name") }}
				<span class="text-danger">*</span>:
			</label>
			<vs-input :data-vv-as="$t('Name')" :placeholder="`${$t('Actions.Input')} ${$t('Name')}`" v-model="localValue.name" class="w-full" :name="`${formName}-name`" v-validate="'required'" />
			<span class="text-danger text-sm" v-show="errors.has(`${formName}-name`)">{{ errors.first(`${formName}-name`) }}</span>
		</div>

		<!-- ASSIGNEES -->
		<div class="mt-5">
			<label class="vs-input--label"> {{ $t("Employee") }}<span class="text-danger">*</span>: </label>
			<cbs-relations-humanroles
				v-model="localValue.assignees"
				:type="1"
				:for="'people'"
				:modelId="parentId"
				:data-vv-as="$t('Employee')"
				:name="`${formName}-assignees`"
				:modelType="$constant.RELATION_MODELS.TODO"
				:placeholder="`${$t('Actions.Choose')} ${$t('Employee').toLowerCase()}`"
				v-validate="'required'"
			/>
			<span class="text-danger text-sm" v-show="errors.has(`${formName}-assignees`)">{{ errors.first(`${formName}-assignees`) }}</span>
		</div>

		<!-- Due time -->
		<div class="due-time-container mt-5">
			<div v-if="todoType === $constant.TODO_TYPE.NORMAL" class="flex flex-wrap justify-between mb-4">
				<vs-radio class="mb-2 sm:mb-0" v-model="localDueTimeMode" :vs-name="formName" :vs-value="0">{{ $t("DueTime") }}</vs-radio>
				<vs-radio v-model="localDueTimeMode" :vs-name="formName" :vs-value="1">{{ $t("DueTimeExactly") }}</vs-radio>
				<div></div>
			</div>
			<h6 v-else class="mb-2 custom-component--title">{{ $t("DueTime") }}<span class="text-danger" v-if="isStartDateRequired">*</span>:</h6>
			<div v-if="localDueTimeMode === 0">
				<div class="due-time-content">
					<div class="flex items-stretch">
						<v-select
							:clearable="false"
							class="bg-white w-1/2 sm:w-1/3"
							:placeholder="`${$t('Actions.Choose')} ${$t('DueTime').toLowerCase()}`"
							v-model="localDueUnit"
							:options="dueUnits"
							:reduce="unit => unit.value"
						>
						</v-select>
						<cbs-input
							v-model.number="localDueValue"
							class="w-1/2 sm:w-2/3"
							:name="`${formName}-due_value`"
							dataType="int"
							v-validate="{ required: isStartDateRequired, regex: '^[1-9]\\d*$' }"
							:data-vv-as="$t('DueTime')"
							:placeholder="`${$t('Placeholder.InputDueValue')}`"
						/>
					</div>
				</div>
				<span class="text-danger text-sm break-normal" v-show="errors.has(`${formName}-due_value`)">{{ errors.first(`${formName}-due_value`) }}</span>
			</div>
			<div v-else>
				<vx-input-group>
					<template slot="append">
						<div class="append-text text-black bg-white">
							<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
						</div>
					</template>
					<cbs-date-picker
						clearable
						class="w-full"
						type="datetime"
						name="start-date"
						prefix-icon="''"
						ref="dateTimePickerRef"
						format="DD-MM-yyyy HH:mm:ss"
						value-format="yyyy-MM-dd HH:mm:ss"
						:data-vv-as="$t('StartDate')"
						:placeholder="`${$t('Actions.Choose')} ${$t('DueTimeExactly').toLowerCase()}`"
						v-model="localDeadline"
					/>
				</vx-input-group>
			</div>
		</div>
	</div>
</template>

<script>
import vSelect from "vue-select";
import moment from "moment";

export default {
	name: "QuickConfigTodoItem",
	inject: ["parentValidator"],
	components: { vSelect },
	props: {
		value: {
			type: Object,
			required: true,
			validator: propValue => propValue.hasOwnProperty("name") && propValue.hasOwnProperty("watchers") && propValue.hasOwnProperty("assignees") && propValue.hasOwnProperty("properties")
		},
		todoType: {
			type: Number,
			required: true
		},
		formName: {
			type: String,
			required: true
		},
		parentId: {
			type: Number,
			required: true
		}
	},
	data() {
		return {
			dueUnits: [
				{ label: this.$t("TypeRepeater.Days"), value: "day" },
				{ label: this.$t("TypeRepeater.Hour"), value: "hour" }
			]
		};
	},
	computed: {
		localValue: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			}
		},
		parsedLocalProperties() {
			return typeof this.localValue.properties === "string" ? JSON.parse(this.localValue.properties) : {};
		},
		localDueTimeMode: {
			get() {
				return this.parsedLocalProperties && this.parsedLocalProperties.due_mode ? this.parsedLocalProperties.due_mode : 0;
			},
			set(val) {
				this.localValue.properties = JSON.stringify({
					...this.parsedLocalProperties,
					due_mode: val
				});
			}
		},
		localDueUnit: {
			get() {
				return this.parsedLocalProperties && this.parsedLocalProperties.due_unit ? this.parsedLocalProperties.due_unit : "day";
			},
			set(val) {
				this.localValue.properties = JSON.stringify({
					...this.parsedLocalProperties,
					due_unit: val
				});
			}
		},
		localDueValue: {
			get() {
				return this.parsedLocalProperties && this.parsedLocalProperties.due_value ? this.parsedLocalProperties.due_value : null;
			},
			set(val) {
				this.localValue.properties = JSON.stringify({
					...this.parsedLocalProperties,
					due_value: Number(val)
				});
			}
		},
		localDeadline: {
			get() {
				return this.parsedLocalProperties && this.parsedLocalProperties.deadline ? moment(this.parsedLocalProperties.deadline).format("YYYY-MM-DD HH:mm:ss") : null;
			},
			set(val) {
				this.localValue.properties = JSON.stringify({
					...this.parsedLocalProperties,
					deadline: val
				});
			}
		},
		isStartDateRequired() {
			return !!(this.localValue.watchers && this.localValue.watchers.length && this.localValue.watchers.some(watcher => watcher.relations && watcher.relations.length));
		}
	},
	methods: {
		triggerClickDatetimePicker() {
			this.$refs.dateTimePickerRef.$children[0].pickerVisible = true;
		}
	},
	created() {
		if (this.parentValidator) {
			this.$validator = this.parentValidator;
		}
	}
};
</script>

<style lang="scss" scoped>
.quick-config-todo-item {
	.due-time-content {
		::v-deep .vs__dropdown-toggle {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
		}

		::v-deep .vs-inputx {
			border-left: 0 !important;
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
		}
	}
}
</style>
